import { IcoShare16 } from '@onefootprint/icons';
import { cx } from 'class-variance-authority';
import { forwardRef, useRef, useState } from 'react';
import Dialog from '../dialog';
import TemplateEditor, { type TemplateEditorRef } from '../template-editor/template-editor';

export type TemplateAutocompleteItem = {
  label: string;
  value: string;
  /** Caller-supplied text shown in the autocomplete dropdown's right-hand column (e.g. a data type). */
  detail?: string;
};

export type TemplateInputProps = {
  id?: string;
  value?: string;
  onChange?: (val: string) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: 'default' | 'compact';
  className?: string;
  autocompleteItems: Record<string, TemplateAutocompleteItem[]>;
  hasError?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  autoFocus?: boolean;
  expandable?: boolean;
};

const TemplateInput = forwardRef<TemplateEditorRef, TemplateInputProps>(
  (
    {
      id,
      value,
      onChange,
      placeholder,
      disabled,
      autocompleteItems,
      size = 'default',
      className,
      hasError,
      onFocus,
      onBlur,
      autoFocus,
      expandable = false,
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [expandedValue, setExpandedValue] = useState(value);

    const openExpandedEditor = () => {
      setIsExpanded(true);
      setExpandedValue(value);
    };

    const closeExpandedEditor = () => {
      setIsExpanded(false);
      setExpandedValue(value);
    };

    const saveExpandedValue = () => {
      onChange?.(expandedValue ?? '');
      closeExpandedEditor();
    };

    return (
      <div ref={containerRef} className="relative group">
        <TemplateEditor
          ref={ref}
          id={id}
          disabled={disabled}
          placeholder={placeholder}
          size={size}
          value={value}
          onChange={onChange}
          variant="input"
          autocompleteItems={autocompleteItems}
          className={cx({ 'group-hover:pr-8 group-focus-within:pr-8': expandable }, className)}
          hasError={hasError}
          onFocus={onFocus}
          onBlur={onBlur}
          autoFocus={autoFocus}
        />
        {expandable && (
          <button
            type="button"
            className="hidden items-center justify-center w-6 h-6 rounded-sm border border-tertiary cursor-pointer hover:bg-hover-control absolute right-1 top-1/2 -translate-y-1/2 group-hover:flex group-focus-within:flex"
            onClick={openExpandedEditor}
          >
            <IcoShare16 />
          </button>
        )}
        {isExpanded && (
          <Dialog
            open={isExpanded}
            onClose={closeExpandedEditor}
            title="Value"
            primaryButton={{ label: 'Save', onClick: saveExpandedValue }}
            secondaryButton={{ label: 'Cancel', onClick: closeExpandedEditor }}
            container={containerRef.current}
          >
            <TemplateEditor
              value={expandedValue}
              onChange={setExpandedValue}
              placeholder={placeholder}
              size="default"
              variant="textarea"
              autocompleteItems={autocompleteItems}
            />
          </Dialog>
        )}
      </div>
    );
  },
);

TemplateInput.displayName = 'TemplateInput';

export default TemplateInput;
